import { useCITypeMapStorage } from '@/entities/srm/ci/@x/tql-query';
import type { TQLFilter } from '@/entities/srm/tql-query';
import {
  CheckIcon,
  Combobox,
  Flex,
  Input,
  InputBase,
  useCombobox,
  type ComboboxItem,
  type SelectProps,
} from '@mantine/core';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  formatTQLNodeLabel,
  getTQLNodeFallbackKey,
  resolveTQLNode,
  resolveTQLNodes,
  type TQLNodeInfo,
} from '../lib/tql-node';
import { TQLNodeLabel } from './tql-node-label';

export interface TQLNodeOption extends ComboboxItem {
  value: string;
  label: string;
  node: TQLNodeInfo;
}

/** Single-line `"<name> · ID: <n>"` label with the localized fallback name. */
export function useTQLNodeLabel({ withId = true } = {}) {
  const { t } = useTranslation();

  return useCallback(
    (node: TQLNodeInfo) =>
      formatTQLNodeLabel(
        node,
        t(getTQLNodeFallbackKey(node), { ns: 'settings' }),
        withId,
      ),
    [t, withId],
  );
}

/**
 * Options for every node of a monitoring template TQL.
 *
 * `selectedNodeId` is appended as an option when it is not in the query any
 * more: without an option the field would have nothing to draw, and an HI link
 * left pointing at a deleted node would look simply unfilled.
 */
export function useTQLNodeOptions(
  query: TQLFilter | undefined,
  selectedNodeId?: null | number | string,
): TQLNodeOption[] {
  const { data: ciTypeMap } = useCITypeMapStorage();
  const getLabel = useTQLNodeLabel();

  return useMemo(() => {
    const toOption = (node: TQLNodeInfo): TQLNodeOption => ({
      value: node.nodeId,
      label: getLabel(node),
      node,
    });

    const options = resolveTQLNodes(query, ciTypeMap).map(toOption);
    const selected = String(selectedNodeId ?? '');

    if (!selected || options.some((option) => option.value === selected)) {
      return options;
    }

    return [...options, toOption(resolveTQLNode(selected, query, ciTypeMap))];
  }, [query, ciTypeMap, selectedNodeId, getLabel]);
}

/** Props of the former `Select`: the field is used with `form.getInputProps`. */
interface TQLNodeSelectProps
  extends Omit<SelectProps, 'data' | 'onChange' | 'renderOption'> {
  options: TQLNodeOption[];
  onChange?: (value: null | string) => void;
}

/**
 * "TQL node" field of the HI link and metric dialogs.
 *
 * Built on `Combobox` rather than `Select` for the sake of the chosen node: a
 * `Select` can only put the plain label string into its input, while here the
 * node is drawn by the same `TQLNodeLabel` as everywhere else — name, dimmed
 * identifier at the right edge and the warning sign of a broken link. The
 * identifier still lives in the option label, so search by number keeps working.
 */
export function TQLNodeSelect({
  options,
  value,
  onChange,
  placeholder,
  disabled,
  clearable = false,
  'data-testid': testId = 'tql-node-select',
  ...props
}: TQLNodeSelectProps) {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');

  const combobox = useCombobox({
    onDropdownOpen: () => combobox.focusSearchInput(),
    onDropdownClose: () => {
      combobox.resetSelectedOption();
      setSearch('');
    },
  });

  const selectedValue = String(value ?? '');
  const selected = options.find((option) => option.value === selectedValue);

  const query = search.trim().toLowerCase();
  const filtered = query
    ? options.filter((option) => option.label.toLowerCase().includes(query))
    : options;

  const searchPlaceholder = t('hiTab.items.tqlEdgePlaceholder', {
    ns: 'settings',
  });

  return (
    <Combobox
      store={combobox}
      withinPortal
      onOptionSubmit={(nextValue) => {
        onChange?.(nextValue);
        combobox.closeDropdown();
      }}
    >
      <Combobox.Target>
        <InputBase
          component='button'
          type='button'
          pointer
          multiline
          label={t('hiTab.items.tqlEdge', { ns: 'settings' })}
          rightSection={
            selected && clearable ? (
              <Combobox.ClearButton
                data-testid={`${testId}-clear`}
                disabled={disabled}
                onMouseDown={(event) => event.preventDefault()}
                onClear={() => onChange?.(null)}
                aria-label={t('action.clear')}
              />
            ) : (
              <Combobox.Chevron />
            )
          }
          rightSectionPointerEvents={selected && clearable ? 'all' : 'none'}
          disabled={disabled}
          data-testid={testId}
          {...(props as Record<string, unknown>)}
          onClick={() => !disabled && combobox.toggleDropdown()}
        >
          {selected ? (
            <TQLNodeLabel
              node={selected.node}
              fullWidth
              fz='sm'
              data-testid={`${testId}-value`}
            />
          ) : (
            <Input.Placeholder>
              {placeholder ?? searchPlaceholder}
            </Input.Placeholder>
          )}
        </InputBase>
      </Combobox.Target>

      <Combobox.Dropdown>
        <Combobox.Search
          value={search}
          onChange={(event) => setSearch(event.currentTarget.value)}
          placeholder={searchPlaceholder}
          data-testid={`${testId}-search`}
        />
        <Combobox.Options mah={260} className='overflow-y-auto'>
          {filtered.length ? (
            filtered.map((option) => (
              <Combobox.Option
                key={option.value}
                value={option.value}
                active={option.value === selectedValue}
                data-testid={`tql-node-option-${option.value}`}
              >
                <Flex component='span' gap='xs' align='center' wrap='nowrap'>
                  {/* Hidden rather than dropped: the names of the options stay
                      in one column. */}
                  <CheckIcon
                    size={10}
                    style={{
                      flexShrink: 0,
                      opacity: option.value === selectedValue ? 1 : 0,
                    }}
                  />
                  <TQLNodeLabel node={option.node} withCIType fz='sm' />
                </Flex>
              </Combobox.Option>
            ))
          ) : (
            <Combobox.Empty>{t('text.nothingFound')}</Combobox.Empty>
          )}
        </Combobox.Options>
      </Combobox.Dropdown>
    </Combobox>
  );
}
